import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LabeledValue } from '@/lib/api';

/** Internal type that adds a stable React key to LabeledValue */
type KeyedLabeledValue = LabeledValue & { _key: string };

let _entryKeyCounter = 0;
function nextEntryKey(): string {
  return `entry-${++_entryKeyCounter}`;
}

/** Strip _key before sending to parent / API */
function stripKeys(entries: KeyedLabeledValue[]): LabeledValue[] {
  return entries.map(({ value, label }) => ({ value, label }));
}

/**
 * Owns a stable React key per row.
 *
 * The parent stores plain `LabeledValue`s — keys are stripped before they leave
 * this component — so deriving keys from the `entries` prop on every render
 * would hand every row a brand-new key on each keystroke. React would then
 * unmount and remount the row, and the input being typed into would lose focus
 * after a single character. The keyed list is therefore kept here and only
 * rebuilt when `entries` arrives from somewhere other than our own `onChange`
 * (dialog opened, form reverted, contact switched).
 */
function useKeyedEntries(entries: LabeledValue[], onChange: (entries: LabeledValue[]) => void) {
  // Held in state rather than a ref because it is DERIVED FROM PROPS DURING
  // RENDER. React may discard and re-run a render (StrictMode does so on every
  // pass); a `setState` from the render body is rolled back with it, whereas a
  // ref mutation would survive into a render that never happened.
  const [keyed, setKeyed] = useState<KeyedLabeledValue[]>([]);
  const [lastEmitted, setLastEmitted] = useState<LabeledValue[] | null>(null);

  let rows = keyed;
  if (entries !== lastEmitted) {
    // `entries` came from outside our own onChange — the dialog opened, the
    // form was reverted, a different contact was selected. Rebuild, reusing
    // keys positionally so untouched rows keep their identity (and their DOM
    // nodes, and the caret inside them). The same array is rendered now and
    // stored for next time, so the two can never disagree.
    rows = entries.map((entry, idx) => ({ ...entry, _key: keyed[idx]?._key ?? nextEntryKey() }));
    setKeyed(rows);
    setLastEmitted(entries);
  }

  const commit = (next: KeyedLabeledValue[]) => {
    const stripped = stripKeys(next);
    setKeyed(next);
    // Remember the exact array handed up. The parent is expected to store it
    // by reference (a bare `useState` setter); when it renders back down,
    // identity tells us this is our own edit echoing rather than an external
    // change, so the keys — and the focused input — survive the round trip.
    setLastEmitted(stripped);
    onChange(stripped);
  };

  return { keyed: rows, commit };
}

export function LabeledEntryList({
  entries,
  onChange,
  labels,
  defaultLabel,
  placeholder,
  fieldLabel,
  inputType = 'text',
  maxItems = 10,
}: {
  entries: LabeledValue[];
  onChange: (entries: LabeledValue[]) => void;
  labels: string[];
  defaultLabel: string;
  placeholder: string;
  fieldLabel: string;
  inputType?: string;
  maxItems?: number;
}) {
  const { keyed, commit } = useKeyedEntries(entries, onChange);

  const updateEntry = (key: string, field: 'value' | 'label', val: string) => {
    commit(keyed.map((e) => (e._key === key ? { ...e, [field]: val } : e)));
  };

  const removeEntry = (key: string) => {
    commit(keyed.filter((e) => e._key !== key));
  };

  const addEntry = () => {
    commit([...keyed, { value: '', label: defaultLabel, _key: nextEntryKey() }]);
  };

  return (
    <div className="space-y-2">
      <Label>{fieldLabel}</Label>
      {keyed.map((entry) => (
        <div key={entry._key} className="flex items-center gap-2">
          <Select value={entry.label} onValueChange={(v) => updateEntry(entry._key, 'label', v)}>
            <SelectTrigger className="w-[110px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {labels.map((l) => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type={inputType}
            value={entry.value}
            onChange={(e) => updateEntry(entry._key, 'value', e.target.value)}
            placeholder={placeholder}
            className="flex-1 h-9"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={() => removeEntry(entry._key)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      {keyed.length < maxItems && (
        <Button type="button" variant="outline" size="sm" className="w-full" onClick={addEntry}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add {fieldLabel.replace(/s$/, '')}
        </Button>
      )}
    </div>
  );
}
